import { Stack, Typography } from '@mui/material';
import { FooterAction, Row } from '../../interfaces';
import {
  MarketingCampaignWithSource,
  PocketflowsCampaignStatus,
} from '../../types/marketing';
import { HeaderCells } from '../../types/ui';
import DropdownMenu from '../../components/global/DropdownMenu';
import { pluralize } from '../global/helper';
import CampaignBadge from '../../components/marketing/CampaignBadge';
import {
  campaignHasEmailStatistics,
  getCampaignLastEditedText,
  getCampaignMediumsText,
} from '../../helpers/marketing';
import CampaignStatistic from '../../components/marketing/CampaignsTable/CampaignStatistic';

export const headerCells = [
  { id: 'campaign', label: 'Campaign' },
  { id: 'status', label: 'Status' },
  { id: 'audience', label: 'Audience' },
  {
    id: 'analytics',
    label: 'Analytics',
    tooltip:
      'Email open rates may not be 100% accurate due to how email clients handle tracking. Some opens might not be counted, while others might be counted automatically by email systems.',
  },
  { id: 'action', label: '' },
] as const satisfies HeaderCells;

const STOPPABLE_STATUSES = [
  'scheduled',
  'started',
] as PocketflowsCampaignStatus[];

export function createRow(
  campaign: MarketingCampaignWithSource,
  {
    onSelectCampaign,
    onEditCampaign,
    onDuplicateCampaign,
    onDeleteCampaign,
    onStopCampaign,
    disableActions,
    hasEmailStatistics,
  }: {
    onSelectCampaign: (campaignId: string) => void;
    onEditCampaign: (campaign: MarketingCampaignWithSource) => void;
    onDuplicateCampaign: (campaign: MarketingCampaignWithSource) => void;
    onDeleteCampaign: (campaignId: string) => void;
    onStopCampaign: (campaignId: string) => void;
    disableActions?: boolean;
    hasEmailStatistics?: boolean;
  },
): Row {
  const { status, statistics } = campaign.source;
  const customersEnrolled = statistics.total_customers_enrolled || 0;
  const medium = getCampaignMediumsText(campaign.mediums, true);

  const options = [
    status === 'draft' && {
      action: () => {
        onEditCampaign(campaign);
      },
      label: 'Edit campaign',
    },
    status !== 'draft' && {
      action: () => {
        onSelectCampaign(campaign.id);
      },
      label: 'View campaign',
    },
    status !== 'draft' && {
      action: () => {
        onDuplicateCampaign(campaign);
      },
      label: 'Duplicate',
    },
    STOPPABLE_STATUSES.includes(status) && {
      action: () => {
        onStopCampaign(campaign.id);
      },
      label: 'Stop',
    },
    status === 'draft' && {
      action: () => {
        onDeleteCampaign(campaign.id);
      },
      label: 'Delete',
    },
  ].filter(Boolean) as FooterAction[];
  const editedText =
    status === 'draft' ? getCampaignLastEditedText(campaign) : '';

  const row = {
    rowId: campaign.id,
    cells: [
      <Stack gap={0.25} maxWidth="400px" overflow="hidden">
        <Typography variant="h5" fontWeight="bold" noWrap>
          {campaign.name}
        </Typography>
        <Typography variant="body2">{medium}</Typography>
        {editedText && (
          <Typography
            variant="body2"
            sx={{
              color: theme => theme.palette.lightOutlineGrey.main,
            }}>
            {editedText}
          </Typography>
        )}
      </Stack>,
      <CampaignBadge campaign={campaign} />,
      <Typography variant="body2">
        {status === 'draft'
          ? '-'
          : `${customersEnrolled} ${pluralize('Customer', customersEnrolled)}`}
      </Typography>,
      campaignHasEmailStatistics(campaign) ? (
        <Stack flexDirection="row" gap={1.5}>
          <CampaignStatistic statistics={statistics} type="opened" />
          <CampaignStatistic statistics={statistics} type="bounced" />
          <CampaignStatistic statistics={statistics} type="clicked" />
        </Stack>
      ) : undefined,
      options.length > 0 ? (
        <DropdownMenu options={options} disabled={disableActions} center />
      ) : null,
    ],
  };

  if (!hasEmailStatistics) {
    row.cells = row.cells.filter(c => c !== undefined);
  }

  return row;
}
